import { useMemo } from "react";
import { Box, CircularProgress, GlobalStyles } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	RowClickedEvent,
} from "ag-grid-community";
import type { RollsetListItem } from "../models/RollshopOperations";
import { ActionsCell } from "./RollsTable";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const buildRollsFullName = (row: RollsetListItem): string => {
	const top = row.TopRollName || "-";
	const bot = row.BottomRollName || "-";
	return `${top} - ${bot}`;
};

// Mirrors the legacy `statusCode` Backbone computed in modules/rollset/rollset.js:
// Status is DERIVED from whether the rollset has its rolls assigned. NOT
// the same as the rollset's operational status (which comes from the SP and
// reflects Available / Prepared for line / In use / etc.).
//   - ASSIGNED:  has top + bottom rolls
//   - PARTIAL:   has just one (top OR bottom)
//   - AVAILABLE: has neither
export const computeAssignmentStatus = (
	row: RollsetListItem,
): "ASSIGNED" | "PARTIAL" | "AVAILABLE" => {
	const top = !!row.TopRollName;
	const bot = !!row.BottomRollName;
	if (top && bot) return "ASSIGNED";
	if (top || bot) return "PARTIAL";
	return "AVAILABLE";
};

const assignmentStatusLabel: Record<
	"ASSIGNED" | "PARTIAL" | "AVAILABLE",
	string
> = {
	ASSIGNED: "Assigned",
	PARTIAL: "Partial",
	AVAILABLE: "Available",
};

const buildColumnDefs = (
	standsByIdName: Map<number, string>,
	millSizesByIdName: Map<number, string>,
): ColDef<RollsetListItem>[] => [
	{
		field: "Name",
		headerName: "Name",
		width: 120,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		sortable: true,
	},
	{
		field: "BarrelDwgFileName",
		headerName: "Barrel Dwg",
		width: 120,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		sortable: true,
	},
	{
		field: "BarrelName",
		headerName: "Barrel Description",
		// flex 1.4 so long descriptions ("Stand 10H for 3 1/2 x 3 1/2 Lite
		// Angle") fit on a single line without forcing horizontal scroll.
		flex: 1.4,
		minWidth: 240,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		sortable: true,
	},
	{
		colId: "RollsFullName",
		headerName: "Rolls",
		flex: 1,
		minWidth: 170,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		sortable: true,
		valueGetter: (p) => (p.data ? buildRollsFullName(p.data) : ""),
	},
	{
		// Look up MillSize name from the catalog (SP returns only MillSizeId).
		colId: "Size",
		headerName: "Size",
		width: 75,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		sortable: true,
		valueGetter: (p) =>
			p.data?.MillSizeId != null
				? (millSizesByIdName.get(p.data.MillSizeId) ?? "")
				: "",
	},
	{
		// Look up Stand name from the catalog (SP returns only StandId / StandNumber;
		// the legacy frontend does Stands.byId[standId].name).
		colId: "Stand",
		headerName: "Stand",
		width: 80,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		sortable: true,
		valueGetter: (p) =>
			// A rollset on a stand the picker no longer offers (S1) keeps its name.
			p.data?.StandId != null
				? (standsByIdName.get(p.data.StandId) ?? p.data.StandName ?? "")
				: "",
	},
	{
		// Computed from rolls presence — matches the legacy rollset.js
		// `statusCode` Backbone computed.
		colId: "AssignmentStatus",
		headerName: "Status",
		width: 100,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		sortable: true,
		valueGetter: (p) =>
			p.data ? assignmentStatusLabel[computeAssignmentStatus(p.data)] : "",
	},
	{
		// SP's actual catRollsetStatus (Available / Prepared for line / In
		// use / Assemble / Pending check). Renamed legacy "Op. Status".
		field: "StatusName",
		headerName: "Op. Status",
		width: 130,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		sortable: true,
	},
];

interface Props {
	rows: RollsetListItem[];
	selectedId: number | null;
	onRowClick: (row: RollsetListItem) => void;
	isLoading: boolean;
	standsByIdName: Map<number, string>;
	millSizesByIdName: Map<number, string>;
	// Bubble up the grid api so the parent can drive Excel export.
	onGridReady?: (api: GridApi<RollsetListItem>) => void;
	onDelete: (row: RollsetListItem) => void;
	deleteDisabled: boolean;
	deleteDisabledReason: string;
}

export const RollsetsTable = ({
	rows,
	selectedId,
	onRowClick,
	isLoading,
	standsByIdName,
	millSizesByIdName,
	onGridReady,
	onDelete,
	deleteDisabled,
	deleteDisabledReason,
}: Props) => {
	const columnDefs = useMemo<ColDef<RollsetListItem>[]>(
		() => [
			...buildColumnDefs(standsByIdName, millSizesByIdName),
			{
				colId: "Actions",
				headerName: "",
				width: 50,
				pinned: "right",
				sortable: false,
				resizable: false,
				cellStyle: { padding: 0 },
				cellRenderer: ActionsCell<RollsetListItem>,
				cellRendererParams: {
					onDelete,
					deleteDisabled,
					deleteDisabledReason,
				},
			},
		],
		[
			standsByIdName,
			millSizesByIdName,
			onDelete,
			deleteDisabled,
			deleteDisabledReason,
		],
	);

	const onCellKeyDown = useArrowRowNavigation<RollsetListItem>(onRowClick);

	const handleRowClicked = (e: RowClickedEvent<RollsetListItem>) => {
		if (e.data) onRowClick(e.data);
	};

	const handleGridReady = (e: GridReadyEvent<RollsetListItem>) => {
		if (selectedId != null) {
			const node = e.api.getRowNode(String(selectedId));
			if (node) node.setSelected(true);
		}
		onGridReady?.(e.api);
	};

	return (
		<Box sx={{ position: "relative", height: "100%" }}>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
					".ag-row-selected, .ag-row-selected:hover": {
						backgroundColor: "rgba(25, 118, 210, 0.12) !important",
					},
				}}
			/>
			{isLoading && rows.length === 0 && (
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 1,
						bgcolor: "rgba(255,255,255,0.7)",
					}}
				>
					<CircularProgress />
				</Box>
			)}
			<AgGridReact<RollsetListItem>
				columnDefs={columnDefs}
				rowData={rows}
				defaultColDef={{
					resizable: true,
					sortable: true,
				}}
				suppressContextMenu
				headerHeight={32}
				rowHeight={30}
				getRowId={(p) => String(p.data.Id)}
				onRowClicked={handleRowClicked}
				onCellKeyDown={onCellKeyDown}
				rowSelection={{
					mode: "singleRow",
					checkboxes: false,
					enableClickSelection: true,
				}}
				rowStyle={{ cursor: "pointer" }}
				onGridReady={handleGridReady}
			/>
		</Box>
	);
};
